import { green, red, yellow } from '@ant-design/colors';
import {
    CheckOutlined,
    MinusCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Select, Table } from 'antd';
import startCase from 'lodash/startCase';
import { useState } from 'react';

import {
    RequirementValidationResultDto,
    WarningDto,
    WorkQualificationResultDto,
} from '@clh/api-client';
import { unwrapError } from '@clh/ui';

import { useApiClient } from '../hooks/use-api-client';
import { useToast } from '../hooks/use-toast';

const { Option } = Select;

export const ValidationResultsTable = (props: {
    validationResults: RequirementValidationResultDto[];
}) => (
    <Table
        dataSource={props.validationResults}
        size="small"
        pagination={{ pageSize: 100, hideOnSinglePage: true }}
        columns={[
            {
                title: 'Requirement',
                dataIndex: 'name',
                render: (v) => startCase(v),
            },
            {
                title: 'Passed',
                dataIndex: 'passed',
                render: (passed, requirement) =>
                    requirement.notApplicable ? (
                        <>N/A</>
                    ) : passed ? (
                        requirement.warnings &&
                        requirement.warnings.length > 0 ? (
                            <WarningOutlined
                                style={{
                                    color: yellow.primary,
                                }}
                            />
                        ) : (
                            <CheckOutlined style={{ color: green.primary }} />
                        )
                    ) : (
                        <MinusCircleOutlined style={{ color: red.primary }} />
                    ),
            },
            {
                title: 'Failure Reason',
                dataIndex: 'failureMessage',
                render: (failureMessage) => <>{failureMessage}</>,
            },
            {
                title: 'Warnings',
                dataIndex: 'warnings',
                render: (warnings) =>
                    warnings && warnings.length > 0
                        ? warnings.map((warning: WarningDto) => (
                              <>
                                  {warning.warningMessage}
                                  <br />
                              </>
                          ))
                        : null,
            },
        ]}
    />
);

type CheckType = 'shift' | 'orientation';

export default function MatchQualificationCheckForm({
    profileId,
}: {
    profileId: string;
}) {
    const api = useApiClient();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<
        WorkQualificationResultDto | undefined
    >(undefined);
    const { notification } = useToast();

    const handleSubmit = async (values: { type: CheckType; id: string }) => {
        if (!api) return;

        setLoading(true);
        setResult(undefined);

        try {
            let res: WorkQualificationResultDto;

            if (values.type === 'shift') {
                const shift = await api.shiftControllerViewShift({
                    shiftId: values.id,
                });

                res =
                    await api.workQualificationControllerGetShiftQualifications(
                        {
                            shiftCandidatesDto: {
                                candidates: [
                                    {
                                        ...shift,
                                        state: shift.facility.address.state,
                                    },
                                ],
                            },
                            nurseProfileId: profileId,
                        }
                    );
            } else {
                const orientation =
                    await api.orientationControllerGetOrientation({
                        orientationId: values.id,
                    });

                const shifts = orientation.orientationSessions.map((os) => ({
                    startTime: os.startTime,
                    endTime: os.endTime,
                    specialtyCode: orientation.specialtyCode ?? undefined,
                    facilityId: orientation.facility.id,
                    state: orientation.facility.address.state,
                }));

                res =
                    await api.workQualificationControllerGetOrientationQualifications(
                        {
                            shiftCandidatesDto: { candidates: shifts },
                            nurseProfileId: profileId,
                        }
                    );
            }

            setResult(res);
        } catch (error: any) {
            notification.error({ message: unwrapError(error), duration: 3 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card size="small" title="Match Qualification Checker">
            <Form
                form={form}
                layout="inline"
                style={{ marginBottom: 16 }}
                onFinish={(v) => void handleSubmit(v)}
            >
                <Form.Item
                    name="type"
                    rules={[
                        { required: true, message: 'Please select a type' },
                    ]}
                    style={{ minWidth: 120 }}
                >
                    <Select placeholder="Select type">
                        <Option value="shift">Shift</Option>
                        <Option value="orientation">Orientation</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="id"
                    rules={[{ required: true, message: 'Please enter an ID' }]}
                    style={{ minWidth: 260 }}
                >
                    <Input
                        placeholder="Enter Shift/Orientation ID"
                        autoComplete="off"
                    />
                </Form.Item>

                <Form.Item>
                    <Button htmlType="submit" loading={loading}>
                        Check Match
                    </Button>
                </Form.Item>
            </Form>

            {result && (
                <>
                    <b>Results:</b>
                    <Alert
                        type={result.qualified > 0 ? 'success' : 'error'}
                        message={
                            result.qualified > 0
                                ? 'Qualified for this shift/orientation'
                                : 'Not qualified for this shift/orientation'
                        }
                        showIcon
                    />
                    {(() => {
                        // Find the first candidate with an error, or else the first candidate
                        const candidateToShow =
                            result.results.find(
                                (candidate) => !candidate.qualified
                            ) || result.results[0];

                        return candidateToShow?.validationResults ? (
                            <ValidationResultsTable
                                validationResults={
                                    candidateToShow.validationResults
                                }
                            />
                        ) : null;
                    })()}
                </>
            )}
        </Card>
    );
}
